import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";
import UFOSegment from "@atlassian/wac-ufo-segment";

import Card from "@/components/wpl/card/src";
import Image from "@/components/wpl/image/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";
import CTAPrimary from "@/patterns/wpl/cta-primary/src";

// added !important to override the default styles since with Compiled we cannot rely on CSS order of precedence
const cardsStyle = css({
    "paddingBlockStart": "64px",
    "paddingBlockEnd": "64px",
    ".row-layout": {
        [media.below.sm]: {
            gap: 0
        }
    },
    ".content-block": {
        img: {
            borderStartStartRadius: "3px",
            borderStartEndRadius: "3px"
        },
        [media.above.sm]: {
            ".text-section": {
                "height": "100%",
                "justifyContent": "space-around",
                "alignText": "stretch",
                "p.contentBlock-body": {
                    "@media (max-width: 1139px)": {
                        minHeight: "68px"
                    },
                    "margin": "0 0 10px"
                }
            }
        }
    },
    ".contentBlock-linkFlex": {
        justifyContent: "center !important",
        margin: "0px"
    },
    ".primary-cta-content": {
        "h2": {
            [media.above.sm]: {
                fontSize: "2.25rem",
                lineHeight: 1.33333333
            },
            [media.below.md]: {
                margin: 0
            },
            fontSize: "1.5rem",
            color: "#253858"
        },
        "h3.contentBlock-eyebrow": {
            [media.above.md]: {
                marginBlockEnd: token("space.200")
            },
            color: "#2684ff",
            fontSize: token("space.200"),
            fontWeight: 700,
            letterSpacing: "1.6px"
        },
        ".text-section": {
            padding: token("space.0"),
            textAlign: "left !important"
        },
        ".contentBlock-linkFlex": {
            justifyContent: "flex-start !important"
        }
    },
    ".text-section": {
        "h3": {
            marginBlockEnd: token("space.200"),
            [media.below.sm]: {
                fontSize: token("space.250"),
                marginBlockEnd: "14px"
            }
        },
        "p.contentBlock-body": {
            [media.below.md]: {
                margin: "0 0 10px"
            }
        },
        "textAlign": "center !important",
        "paddingInlineStart": token("space.400"),
        "paddingInlineEnd": token("space.400"),
        "paddingBlockEnd": token("space.400"),
        "paddingBlockStart": token("space.200")
    },
    ".custom-image": {
        borderRadius: "3px",
        boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.1)"
    },
    ".custom-card": {
        "borderRadius": "3px",
        "boxShadow": "1px 1px 6px rgba(0, 0, 0, 0.1) !important",
        "height": "100%",
        "&.mobile": {
            display: "none"
        },
        [media.below.sm]: {
            "&.mobile": {
                display: "block"
            },
            "&.desktop": {
                display: "none"
            }
        }
    },
    ".custom-card:hover": {
        transition: "box-shadow 200ms ease-in",
        boxShadow: "0px 4px 6px 3px #42526e29 !important"
    },
    ".custom-cta-primary": {
        "padding": token("space.0"),
        [media.above.md]: {
            padding: "64px 0 0 0"
        },
        ".contentBlock-linkFlex": {
            marginBlockEnd: 0
        }
    },
    ".link-wrapper": {
        "svg": {
            display: "none"
        },
        "span": {
            color: "#0052cc",
            fontWeight: 400
        },
        "span:hover": {
            color: "#0065ff",
            textShadow: "none"
        }
    },
    ".column": {
        paddingBlockStart: token("space.200"),
        paddingBlockEnd: token("space.200"),
        position: "relative",
        minHeight: "1px"
    },
    ".column-cta-primary": {
        "paddingBlockStart": token("space.200"),
        "paddingBlockEnd": token("space.200"),
        [media.below.sm]: {
            paddingBlockStart: token("space.400")
        },
        [media.only.sm]: {
            gridColumn: "1 / 3",
            paddingBlockEnd: 0
        },
        "+ .row-layout": {
            [media.only.sm]: {
                gridColumn: "1 / 3"
            }
        }
    },
    ".column-large-card": {
        "textAlign": "center !important",
        [media.only.sm]: {
            gridColumn: "1 / 3"
        },
        ".content-block": {
            [media.above.sm]: {
                "p.contentBlock-body": {
                    minHeight: "30px"
                }
            }
        }
    },
    ".fade-up": {
        [media.above.sm]: {
            opacity: 0,
            transform: "translate3d(0, 60px, 0)",
            transition: "0.5s transform ease-in-out, 0.5s opacity ease-in-out",
            transitionDelay: "0.3s"
        }
    },
    ".in-view": {
        [media.above.sm]: {
            opacity: 1,
            transform: "translate3d(0, 0, 0)"
        }
    }
});

export const Cards = () => {
    const intl = useIntl();

    // Row 1

    // Atlassian Community Card
    const atlassianCommunityImageAddressMobile =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/CommunityMobile_360x235%402x.webp";
    const atlassianCommunityImageAddressDesktop =
        "https://wac-cdn.atlassian.com/dam/jcr:549ce2f8-cc5c-4a06-8ce3-ad2b938ae0a5/Community@2x.jpg?cdnVersion=1223";

    const atlassianCommunityLink1 = intl.formatMessage({
        id: "HomePageTemplate.cardsAtlassianCommunityLink1",
        defaultMessage: "Search the forum"
    });
    const atlassianCommunityLink2 = intl.formatMessage({
        id: "HomePageTemplate.cardsAtlassianCommunityLink2",
        defaultMessage: "Join our community"
    });
    const [atlassianCommunityRef, atlassianCommunityInView] = useInView({
        triggerOnce: true
    });
    const atlassianCommunityClass = `column column-large-card fade-up ${
        atlassianCommunityInView ? "in-view" : ""
    }`;

    // Row 2

    // Work Life Card
    const workLifeImageAddress =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/Blog_360x265%402x.webp";
    const workLifeLink = intl.formatMessage({
        id: "HomePageTemplate.cardsWorkLifeLink",
        defaultMessage: "Read our blog"
    });
    const [workLifeRef, workLifeInView] = useInView({ triggerOnce: true });
    const workLifeClass = `column column-small-card fade-up ${
        workLifeInView ? "in-view" : ""
    }`;

    // Atlassian Team Playbook Card
    const atlassianTeamPlaybookImageAddress =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/Team%2520Playbook_360x265%402x.webp";
    const atlassianTeamPlaybookLink = intl.formatMessage({
        id: "HomePageTemplate.cardsAtlassianTeamPlaybookLink",
        defaultMessage: "Check team health"
    });
    const [atlassianTeamPlaybookRef, atlassianTeamPlaybookInView] = useInView({
        triggerOnce: true
    });
    const atlassianTeamPlaybookClass = `column column-small-card fade-up ${
        atlassianTeamPlaybookInView ? "in-view" : ""
    }`;

    // Distributed Work Card
    const distributedWorkImageAddress =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/team_anywear_illo.webp";
    const distributedWorkLink = intl.formatMessage({
        id: "HomePageTemplate.cardsDistributedWorkLink",
        defaultMessage: "Our evidence-based approach"
    });
    const [distributedWorkRef, distributedWorkInView] = useInView({
        triggerOnce: true
    });
    const distributedWorkClass = `column column-small-card fade-up ${
        distributedWorkInView ? "in-view" : ""
    }`;

    // Row 3

    // Team24 Image Card
    const team24Address =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/team24promobanner.webp-3262748562.webp";
    const [team24Ref, team24InView] = useInView({ triggerOnce: true });
    const team24Class = `column column-image fade-up ${
        team24InView ? "in-view" : ""
    }`;

    // High Velocity Image Card
    const highVelocityAddress =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/high-velocity.webp";
    const [highVelocityRef, highVelocityInView] = useInView({
        triggerOnce: true
    });
    const highVelocityClass = `column column-image fade-up ${
        highVelocityInView ? "in-view" : ""
    }`;

    const cardsEventsLink = intl.formatMessage({
        id: "HomePageTemplate.cardsEventsLink",
        defaultMessage: "Learn more"
    });

    return (
        <UFOSegment name="cards">
            <section css={cardsStyle}>
                <Row layout="4-8">
                    <div className="column-cta-primary">
                        <CTAPrimary
                            eyebrowText={intl.formatMessage({
                                id: "HomePageTemplate.cardsTeamEssentialsEyebrow",
                                defaultMessage: "TEAM ESSENTIALS"
                            })}
                            headerText={intl.formatMessage({
                                id: "HomePageTemplate.cardsTeamEssentialsHeader",
                                defaultMessage:
                                    "Explore resources to unleash the potential of your team"
                            })}
                            bodyText=""
                            className="custom-cta-primary"
                        />
                    </div>
                    <div
                        ref={atlassianCommunityRef}
                        className={atlassianCommunityClass}
                    >
                        <Card
                            headerText={intl.formatMessage({
                                id: "HomePageTemplate.cardsAtlassianCommunityHeader",
                                defaultMessage: "Atlassian Community"
                            })}
                            bodyText={intl.formatMessage({
                                id: "HomePageTemplate.cardsAtlassianCommunityBody",
                                defaultMessage:
                                    "Connect globally and meet locally to get more out of our products."
                            })}
                            pictureProps={{
                                image: {
                                    src: atlassianCommunityImageAddressDesktop,
                                    alt: ""
                                }
                            }}
                            backgroundColor="y50"
                            aspectRatio="none"
                            ctaProps={{
                                primaryLinkProps: {
                                    text: atlassianCommunityLink1,
                                    href: "https://community.atlassian.com/"
                                },
                                secondaryLinkProps: {
                                    text: atlassianCommunityLink2,
                                    href: "/community"
                                }
                            }}
                            className="custom-card desktop"
                        />
                        <Card
                            headerText={intl.formatMessage({
                                id: "HomePageTemplate.cardsAtlassianCommunityHeader",
                                defaultMessage: "Atlassian Community"
                            })}
                            bodyText={intl.formatMessage({
                                id: "HomePageTemplate.cardsAtlassianCommunityBody",
                                defaultMessage:
                                    "Connect globally and meet locally to get more out of our products."
                            })}
                            pictureProps={{
                                image: {
                                    src: atlassianCommunityImageAddressMobile,
                                    alt: ""
                                }
                            }}
                            backgroundColor="y50"
                            aspectRatio="none"
                            ctaProps={{
                                primaryLinkProps: {
                                    text: atlassianCommunityLink1,
                                    href: "https://community.atlassian.com/"
                                },
                                secondaryLinkProps: {
                                    text: atlassianCommunityLink2,
                                    href: "/community"
                                }
                            }}
                            className="custom-card mobile"
                        />
                    </div>
                </Row>
                <Row layout="4-4-4">
                    <div
                        ref={workLifeRef}
                        className={workLifeClass}
                        style={{ transitionDelay: "0.5s" }}
                    >
                        <Card
                            headerText={intl.formatMessage({
                                id: "HomePageTemplate.cardsWorkLifeHeader",
                                defaultMessage: "Work Life"
                            })}
                            bodyText={intl.formatMessage({
                                id: "HomePageTemplate.cardsWorkLifeBody",
                                defaultMessage:
                                    "Real-life advice, inspiration, and stories from the working world today."
                            })}
                            pictureProps={{
                                image: {
                                    src: workLifeImageAddress,
                                    alt: ""
                                }
                            }}
                            backgroundColor="p50"
                            aspectRatio="none"
                            ctaProps={{
                                primaryLinkProps: {
                                    text: workLifeLink,
                                    href: "https://www.atlassian.com/blog"
                                }
                            }}
                            className="custom-card"
                        />
                    </div>
                    <div
                        ref={atlassianTeamPlaybookRef}
                        className={atlassianTeamPlaybookClass}
                        style={{ transitionDelay: "0.7s" }}
                    >
                        <Card
                            headerText={intl.formatMessage({
                                id: "HomePageTemplate.cardsAtlassianTeamPlaybookHeader",
                                defaultMessage: "Atlassian Team Playbook"
                            })}
                            bodyText={intl.formatMessage({
                                id: "HomePageTemplate.cardsAtlassianTeamPlaybookBody",
                                defaultMessage:
                                    "Solve common team challenges with these group exercises."
                            })}
                            pictureProps={{
                                image: {
                                    src: atlassianTeamPlaybookImageAddress,
                                    alt: ""
                                }
                            }}
                            backgroundColor="t50"
                            aspectRatio="none"
                            ctaProps={{
                                primaryLinkProps: {
                                    text: atlassianTeamPlaybookLink,
                                    href: "/team-playbook"
                                }
                            }}
                            className="custom-card"
                        />
                    </div>
                    <div
                        ref={distributedWorkRef}
                        className={distributedWorkClass}
                        style={{ transitionDelay: "0.9s" }}
                    >
                        <Card
                            headerText={intl.formatMessage({
                                id: "HomePageTemplate.cardsDistributedWorkHeader",
                                defaultMessage: "Distributed Work"
                            })}
                            bodyText={intl.formatMessage({
                                id: "HomePageTemplate.cardsDistributedWorkBody",
                                defaultMessage:
                                    "Advance how your distributed teams collaborate."
                            })}
                            pictureProps={{
                                image: {
                                    src: distributedWorkImageAddress,
                                    alt: ""
                                }
                            }}
                            backgroundColor="b50"
                            aspectRatio="none"
                            ctaProps={{
                                primaryLinkProps: {
                                    text: distributedWorkLink,
                                    href: "/solutions/distributed"
                                }
                            }}
                            className="custom-card"
                        />
                    </div>
                </Row>
                <Row layout="4-8">
                    <div className="column-cta-primary">
                        <CTAPrimary
                            eyebrowText={intl.formatMessage({
                                id: "HomePageTemplate.cardsEventsEyebrow",
                                defaultMessage: "EVENTS"
                            })}
                            headerText={intl.formatMessage({
                                id: "HomePageTemplate.cardsEventsHeader",
                                defaultMessage:
                                    "Hear from today's fearless builders and innovators"
                            })}
                            bodyText=""
                            ctaProps={{
                                primaryLinkProps: {
                                    text: cardsEventsLink,
                                    href: useLocalizedHref("/company/events")
                                }
                            }}
                            className="custom-cta-primary"
                        />
                    </div>
                    <Row layout="6-6">
                        <div ref={team24Ref} className={team24Class}>
                            <a href="https://events.atlassian.com/team?ref=Team_24&utm_source=marketing&utm_campaign=acquisition&utm_content=WAC_homepage_page_feature&utm_medium=web">
                                <Image
                                    loading="lazy"
                                    src={team24Address}
                                    alt={intl.formatMessage({
                                        id: "HomePageTemplate.cardsEventsTeam24Alt",
                                        defaultMessage: "Team '24 event"
                                    })}
                                    aspectRatio="1/1"
                                    className="custom-image"
                                />
                            </a>
                        </div>
                        <div
                            ref={highVelocityRef}
                            className={highVelocityClass}
                            style={{ transitionDelay: "0.5s" }}
                        >
                            <a href="https://events.atlassian.com/highvelocity?ref=High_Velocity&utm_source=marketing&utm_campaign=acquisition&utm_content=wac_event_tile&utm_medium=website">
                                <Image
                                    loading="lazy"
                                    src={highVelocityAddress}
                                    alt={intl.formatMessage({
                                        id: "HomePageTemplate.cardsEventsHighVelocityAlt",
                                        defaultMessage:
                                            "Atlassian High Velocity event"
                                    })}
                                    aspectRatio="1/1"
                                    className="custom-image"
                                />
                            </a>
                        </div>
                    </Row>
                </Row>
            </section>
        </UFOSegment>
    );
};
